import type { CreateSalesTransactionPayment } from '@/api/handlers/resources/sales/requests';

/**
 * Pure presentational config + money helpers for the POS payment modal. No
 * React, no state — just the method list the view renders, the cash-numpad
 * string math, and the payment-array builder. Kept here (not the `.tsx`) so the
 * render-only sections stay config-free and the math stays unit-testable.
 */

/**
 * The UI payment methods. These are the seller-facing choices, not the BosAPI
 * `PaymentType` enum: `combined` splits the due across a `Cash` + `Card` pair
 * (see `buildPayments`). There is no transfer: the accountant sign-off keeps
 * Invoice/Voucher tenders refused at the POS.
 */
export type PaymentMethod = 'cash' | 'card' | 'combined';

export type PaymentMethodMeta = {
    key: PaymentMethod;
    labelKey: string;
    icon: string;
};

/** Order shown in the method selector. */
export const PAYMENT_METHODS: PaymentMethodMeta[] = [
    {
        key: 'cash',
        labelKey: 'seller.payment.cash',
        icon: 'solar:wad-of-money-bold-duotone',
    },
    {
        key: 'card',
        labelKey: 'seller.payment.card',
        icon: 'solar:card-bold-duotone',
    },
    {
        key: 'combined',
        labelKey: 'seller.payment.combined',
        icon: 'solar:layers-minimalistic-bold-duotone',
    },
];

/** Common tendered notes offered as one-tap shortcuts beside the cash numpad. */
export const QUICK_CASH_AMOUNTS = [5, 10, 20, 50, 100];

/** The two combined-split legs, in the order `Tab`/`↑`/`↓` traverse them (bos-seller#59). */
export const COMBINED_LEGS: readonly ['cash', 'card'] = ['cash', 'card'];

/**
 * The neighbouring leg `↑`/`↓` (`direction` `1`/`-1`) would move to, or `null`
 * at either end — traversal doesn't wrap (bos-seller#59).
 */
export const nextCombinedLeg = (
    current: (typeof COMBINED_LEGS)[number],
    direction: 1 | -1,
): (typeof COMBINED_LEGS)[number] | null =>
    COMBINED_LEGS[COMBINED_LEGS.indexOf(current) + direction] ?? null;

/**
 * The leg a split-payment `↑`/`↓` lands on, or `null` when `key` isn't one of
 * them or there's no leg that way. From the method group (`'methods'`), `↓`
 * enters the first leg and `↑` the last, so the arrows never change the method
 * while the split is selected (`←`/`→` still do); from a leg they step to its
 * neighbour (`nextCombinedLeg`, no wraparound).
 */
export const combinedLegForArrow = (
    key: string,
    from: (typeof COMBINED_LEGS)[number] | 'methods',
): (typeof COMBINED_LEGS)[number] | null => {
    const direction = key === 'ArrowDown' ? 1 : key === 'ArrowUp' ? -1 : null;
    if (direction === null) {
        return null;
    }
    if (from === 'methods') {
        return direction === 1 ? COMBINED_LEGS[0] : COMBINED_LEGS[1];
    }
    return nextCombinedLeg(from, direction);
};

/**
 * `Intl` options for the combined-split cash/card fields — GEL currency shown
 * with the `₾` narrow symbol (en-US would otherwise print the `GEL` code).
 */
export const PAYMENT_CASH_FORMAT: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: 'GEL',
    currencyDisplay: 'narrowSymbol',
};

/**
 * Sub-cent tolerance for money comparisons — floating-point sums of cash never
 * land exactly on zero, so treat anything under half a tetri as equal.
 */
export const MONEY_EPSILON = 0.005;

/** Round to 2 decimals (GEL) the way the server does, so amounts match its recompute. */
export const toMoney = (value: number): number => {
    const rounded = Math.round(value * 100) / 100;
    // Normalise a `-0` (e.g. an exactly-covered split) to `0` so no figure ever
    // renders as `-0.00`.
    return rounded === 0 ? 0 : rounded;
};

/** Change owed back to the customer for a cash tender; never negative. */
export const computeChange = (due: number, received: number): number =>
    Math.max(0, toMoney(received - due));

/**
 * A numpad-driven amount: the raw `text` (so the display can show in-progress
 * input like `12.` faithfully), and whether a typed key was refused for not
 * fitting (`overflow`) — never confirmable, and the dialog says why.
 */
export type PadEntry = { text: string; overflow: boolean };

export const EMPTY_PAD_ENTRY: PadEntry = { text: '', overflow: false };

/**
 * An amount the app fills in itself (Exact, a quick note, a split leg seeded
 * with its share of the due). Never capped: the cap is on typing, and a due of
 * ₾1,000,000 or more is still the due.
 */
export const filledPadEntry = (text: string): PadEntry => ({
    text,
    overflow: false,
});

/**
 * The most a typed amount holds: six integer digits (₾999,999) and two
 * decimals. A key past either is a barcode scanned while payment was open,
 * not money; the cap also keeps every typed entry inside the safe-integer
 * range.
 */
export const PAD_MAX_INTEGER_DIGITS = 6;
const PAD_MAX_DECIMALS = 2;

/**
 * Append a typed numpad key. Allows a single decimal point and avoids
 * leading-zero runs (`00`). A digit that doesn't fit — a seventh integer digit
 * or a third decimal — is refused and flags `overflow`, and nothing is
 * trimmed into a plausible amount (`50.` + a scan must not become `50.48`).
 * An overflowed entry takes no further key until `deletePadKey` clears it,
 * so the rest of a scan goes nowhere.
 */
export const appendPadKey = (entry: PadEntry, key: string): PadEntry => {
    const { text, overflow } = entry;
    if (overflow) {
        return entry;
    }
    if (key === '.') {
        if (text.includes('.')) {
            return entry;
        }
        return { text: text === '' ? '0.' : `${text}.`, overflow: false };
    }
    if (text === '0') {
        return { text: key, overflow: false };
    }
    const [integer = '', decimals] = text.split('.');
    const isFull =
        decimals === undefined
            ? integer.length >= PAD_MAX_INTEGER_DIGITS
            : decimals.length >= PAD_MAX_DECIMALS;
    return { text: isFull ? text : text + key, overflow: isFull };
};

/**
 * Drop the last character — or the whole entry once it overflowed, so one
 * `Backspace` recovers from a stray scan.
 */
export const deletePadKey = (entry: PadEntry): PadEntry =>
    entry.overflow
        ? EMPTY_PAD_ENTRY
        : { text: entry.text.slice(0, -1), overflow: false };

/** Parse the cash-entry buffer to a number; an empty / partial entry reads 0. */
export const parseCash = (current: string): number => {
    const value = Number(current);
    return Number.isFinite(value) ? value : 0;
};

/**
 * Build the `payments` array for `POST /sales/transactions`. Money is
 * server-authoritative: each payment carries the amount applied to the sale
 * (for cash that's the due, not the tendered — change is handled at the till),
 * and the server cross-checks the sum against its recomputed total. `combined`
 * drops a zero-value leg so a one-sided split posts a single payment.
 */
export const buildPayments = (
    method: PaymentMethod,
    due: number,
    split: { cash: number; card: number },
): CreateSalesTransactionPayment[] => {
    switch (method) {
        case 'cash':
            return [{ type: 'Cash', amount: toMoney(due) }];
        case 'card':
            return [{ type: 'Card', amount: toMoney(due) }];
        case 'combined': {
            const payments: CreateSalesTransactionPayment[] = [];
            const cash = toMoney(split.cash);
            const card = toMoney(split.card);
            if (cash > 0) {
                payments.push({ type: 'Cash', amount: cash });
            }
            if (card > 0) {
                payments.push({ type: 'Card', amount: card });
            }
            return payments;
        }
    }
};
